import { createMemo } from "solid-js"
import dijkstraDirectionCharsLowerCase from "../../lib/core/direction/dijkstraDirectionCharsLowerCase"
import Unexpected from "../../lib/Exception/Unexpected.class"
import isNumberListSame from "../signal/equals/isNumberListSame"
import createSharedCachedFactory from "./createSharedCachedFactory"
import { createSharedTileGraphNodeCollectionMemo } from "./createSharedTileGraphNodeCollectionMemo"
import { useSharedTerrainBaseFloorElevationListMemo } from "./useSharedTerrainBaseFloorElevationListMemo"

export const useSharedManhattanDistanceToLandListMemo = createSharedCachedFactory((engine) => {
  const getTileGraphNodesList = createSharedTileGraphNodeCollectionMemo(engine)
  const getBaseElevationList = useSharedTerrainBaseFloorElevationListMemo(engine)

  const memo = createMemo<Int16Array>(() => {
    // console.time('useSharedManhattanDistanceToLandListMemo.memo')
    const tpNodeList = getTileGraphNodesList()
    const baseElevationList = getBaseElevationList()
    const len = baseElevationList.length
    const list = new Int16Array(len)

    // Initialize all to -1 (sentinel value for unprocessed tiles)
    list.fill(-1)

    const queue: number[] = []

    // Mark land tiles as 0, and find sea tiles adjacent to land
    for (let index = 0; index < len; index++) {
      const elevation = baseElevationList[index]
      if (elevation > 0) {
        // Land tiles are always zero
        list[index] = 0
        const node = tpNodeList[index]
        if (!node) continue

        // Check neighbors — only add adjacent sea tiles to queue
        for (const dir of dijkstraDirectionCharsLowerCase) {
          const neighbor = node[dir]
          const nIndex = neighbor?.index
          if (nIndex !== undefined && baseElevationList[nIndex] <= 0 && list[nIndex] === -1) {
            list[nIndex] = 1
            queue.push(nIndex)
          }
        }
      }
    }

    // BFS from initial sea-adjacent-to-land tiles
    let maxAttempts = len * 2 // Fewer needed since land is not included

    let qStart = 0
    while (qStart < queue.length) {
      if (!(maxAttempts-- > 0)) throw new Unexpected('maxAttempts exceeded')

      const index0 = queue[qStart++]
      const node0 = tpNodeList[index0]
      if (!node0) continue

      const distance0 = list[index0]

      for (const dir of dijkstraDirectionCharsLowerCase) {
        const neighbor = node0[dir]
        const index1 = neighbor?.index
        if (
          index1 !== undefined &&
          baseElevationList[index1] <= 0 &&  // only propagate through sea
          list[index1] === -1
        ) {
          list[index1] = distance0 + 1
          queue.push(index1)
        }
      }
    }

    // console.timeEnd('useSharedManhattanDistanceToLandListMemo.memo')
    return list
  }, new Int16Array(0), {
    equals: isNumberListSame,
  })

  // createEffect(() => {
  //   console.log('getManhattanDistanceToLandList.effect.value', memo())
  // })

  return memo
})